import { useEffect, useState } from "react";
import { CSVLink } from "react-csv";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useForm } from "react-hook-form";
import { BiSolidDownArrow, BiSolidUpArrow } from "react-icons/bi";
import { FiCalendar, FiDownload, FiSearch } from "react-icons/fi";
import { Link } from "react-router-dom";
import InputBox from "../../../Components/Input";
import SelectDropdown from "../../../Components/InputSelect";
import Loader from "../../../Components/Loader";
import Pagination from "../../../Components/Pagination";
import usePageFilter from "../../../hooks/usePageFilter";
import { API_ENDPOINTS } from '../../../utils/api';
import createAxiosInstance from "../../../utils/axiosInstance";
import { formatDateShort, formatToUSD } from "../../../utils/formatters";
import CreateNewNeed from "../create-need";

const NeedsHome = () => {
  const axiosInstance = createAxiosInstance();

  const [recentNeedList, setRecentNeedList] = useState([]);
  const [cacheResult, setCacheResult] = useState<any[]>([]);
  const [rowPerPage, setRowPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoding, setIsLoding] = useState(true);
  const [defaultValues, setDefaultValues] = useState({
    search: "",
    status: "",
    startDate: null,
    endDate: null
  });

  const {
    register,
    watch,
    setValue,
    getValues
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues,
  });

  const watchSearch = watch("search");
  const watchStatus = watch("status");
  const watchStartDate = watch("startDate");
  const watchEndDate = watch("endDate");

  useEffect(() => {
    filterResult();
  }, [watchStatus, watchSearch, watchStartDate, watchEndDate]);

  const filterResult = () => {
    let result: any = cacheResult;
    if (getValues("search") !== "") {
      result = result.filter(
        (item: any) =>
          item.id.toString().includes(getValues("search")) ||
          formatDateShort(item.date).includes(getValues("search")) ||
          formatToUSD(item.amount)?.toString().includes(getValues("search")) ||
          item.status.toLowerCase().includes(getValues("search").toLowerCase()) ||
          item.reason.toLowerCase().includes(getValues("search").toLowerCase())
      );
    }
    if (getValues("status") !== "") {
      result = result.filter(
        (item: any) =>
          item.status === getValues("status")
      );
    }
    const startDate = getValues("startDate");
    const endDate = getValues("endDate");
    if (startDate !== null && endDate !== null) {
      result = result.filter((item: any) => {
        const itemDate = new Date(formatDateShort(item.date));
        return itemDate >= new Date(startDate) && itemDate <= new Date(endDate);
      });
    }
    setTotalPages(Math.ceil(result.length / rowPerPage));
    handleSort(sortColumn, result, sortOrder);
  };

  // Dropdown options
  const dropdownOptions = [
    { value: "", label: "All Alerts" },
    { value: "Open", label: "Open Alerts" },
    { value: "Closed", label: "Fulfilled Alerts" },
    { value: "Cancelled", label: "Cancelled Alerts" },
  ];

  const headers = [
    { label: "Id", key: "id" },
    { label: "Date", key: "date" },
    { label: "Amount", key: "amount" },
    { label: "Status", key: "status" },
    { label: "Nights", key: "nights" },
    { label: "Reason", key: "reason" },
  ];

  // Sorting State
  const [sortColumn, setSortColumn] = useState<any>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Handle Sorting
  const handleSort = (column: keyof typeof recentNeedList[0], result: any = recentNeedList, passed_order?: "asc" | "desc") => {
    let order: any = passed_order || sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    if (passed_order) {
      order = passed_order;
    }
    setSortColumn(column);
    setSortOrder(order);

    // Sort Data
    const sortedData: any = [...result].sort((a, b) => {
      if (a[column] < b[column]) return order === "asc" ? -1 : 1;
      if (a[column] > b[column]) return order === "asc" ? 1 : -1;
      return 0;
    });

    setRecentNeedList(sortedData);
  };

  const updateStartDate = (date: any) => {
    setDefaultValues((prev) => ({ ...prev, startDate: date }));
    setValue("startDate", date);
  };

  const updateEndDate = (date: any) => {
    setDefaultValues((prev) => ({ ...prev, endDate: date }));
    setValue("endDate", date);
  };

  useEffect(() => {
    register("startDate");
    register("endDate");
    getNeedList();
  }, []);

  const getNeedList = async () => {
    try {
      setIsLoding(true);
      const response = await axiosInstance.get(`${API_ENDPOINTS.SAFENIIGHT.GETRECENTNEEDS}?status=*&expand=true&orderBy=sequenceId+desc`);

      const data: any = [];

      response.data.map((element: any) => {
        const obj = {
          needId: element.needId,
          id: element.sequenceId,
          date: element.createdAt,
          amount: element.assessments[0].amount,
          status: element.status === "open" ? "Open" : element.status === "closed" ? "Closed" : "Cancelled",
          nights: element.assessments[0].numberOfBednight ? Number(element.assessments[0].numberOfBednight) : 0,
          reason: `${element.assessments[0].needReason} ${element.assessments[0].needReason == "Other" && element.assessments[0].description ? `: ${element.assessments[0].description}` : ""}`
        };
        data.push(obj);
      });

      setTotalPages(Math.ceil(data.length / rowPerPage));
      handleSort("id", data, "desc");
      setCacheResult(data);
      setIsLoding(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const filteredRequests = usePageFilter(
    recentNeedList,
    (currentPage - 1) * rowPerPage,
    rowPerPage
  );

  return (
    <>
      {isLoding && <Loader />}
      <div className="container py-4 md:py-10 2xl:py-[70px]">
        <div className="mb-3 md:mb-4 2xl:mb-8 flex justify-center">
          <CreateNewNeed updateFeed={getNeedList} />
        </div>
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-gray-900 font-semibold text-xl">Alerts</h3>
          {(recentNeedList && recentNeedList.length > 0) && (
            <CSVLink filename={`needs.csv`} data={recentNeedList} headers={headers}>
              <button className='btn btn-outline-gray py-2 2xl:py-3'>
                <FiDownload size={20} />
                Export to Excel
              </button>
            </CSVLink>
          )}
        </div>
        <div className="my-3 flex flex-wrap -mx-3">
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> View:</label>
            <div className="flex-1">
              <SelectDropdown
                options={dropdownOptions}
                name="status"
                placeholder="Choose an option"
                value={watch("status")}
                register={register}
                setValue={setValue}
              />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> From :</label>
            <div className="flex-1 relative">
              <DatePicker selected={defaultValues.startDate} onChange={(date: Date | null) => updateStartDate(date)} calendarClassName="ssc-custom-calendar" className='form-control w-full' />
              <FiCalendar size={20} className='text-gray-600 absolute top-1/2 -translate-y-1/2 right-3' />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> To :</label>
            <div className="flex-1 relative">
              <DatePicker selected={defaultValues.endDate} onChange={(date: Date | null) => updateEndDate(date)} calendarClassName="ssc-custom-calendar" className='form-control w-full' />
              <FiCalendar size={20} className='text-gray-600 absolute top-1/2 -translate-y-1/2 right-3' />
            </div>
          </div>
          <div className="w-full sm:w-1/2 lg:w-1/4 px-3 flex items-center gap-3 justify-center mb-3 lg:mb-0">
            <label htmlFor="whatFees" className='block text-md font-medium text-gray-500 text-end'> Search :</label>
            <div className="flex-1">
              <InputBox
                type="search"
                placeholder="Search"
                iconRight={<FiSearch />}
                {...register('search')}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="relative md:overflow-hidden md:border md:border-borderGray rounded-xl">
          <table className="table_responsive_block w-full text-xs 2xl:text-sm text-left">
            <thead className="text-xs 2xl:text-sm text-gray-600 bg-gray-100 border-b">
              <tr>
                {headers.map((col: any) => (
                  <th key={col.label} onClick={() => handleSort(col.key as keyof typeof recentNeedList[0])} className="px-4 2xl:px-6 py-3 font-medium cursor-pointer">
                    {col.label}
                    {sortColumn === col.key && (sortOrder === "desc" ? <BiSolidDownArrow className="inline text-xs ml-1" /> : <BiSolidUpArrow className="inline text-xs ml-1" />)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRequests.map((row: any) => (
                <tr key={row.id} className="border-b bg-white">
                  <td data-title='Id' className="px-4 2xl:px-6 py-3 font-medium text-primary cursor-pointer whitespace-nowrap"> <Link to={`/safenight/needs/need-details/${row.needId}`}> {row.id} </Link></td>
                  <td data-title='Date' className="text-gray-800 px-4 2xl:px-6 py-3">{formatDateShort(row.date)}</td>
                  <td data-title='Amount' className="text-gray-800 px-4 2xl:px-6 py-3">{formatToUSD(row.amount)}</td>
                  <td data-title='Status' className="text-gray-800 px-4 2xl:px-6 py-3">
                    <span className={`badge ${row.status === "Open" ? "badge-success" : "badge-danger"}`}>
                      {row.status}
                    </span>
                  </td>
                  <td data-title='Nights' className="text-gray-800 px-4 2xl:px-6 py-3">{row.nights}</td>
                  <td data-title='Reason' className="text-gray-800 px-4 2xl:px-6 py-3">{row.reason}</td>
                </tr>
              ))}
              {filteredRequests.length === 0 && (
                <tr>
                  <td colSpan={headers.length} className="text-center py-4 text-gray-500">There are no needs.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-5 flex justify-between items-center">
          <div></div>
          <Pagination totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      </div>
    </>
  );
};

export default NeedsHome;
